/** @jsx jsx */
import {
  Box,
  Button,
  Checkbox,
  FiltersIcon,
  Flex,
  FormGroup,
  FormLabel,
  FullScreenModal,
  FullScreenModalContainer,
  FullScreenModalHeader,
  FullScreenModalHeading,
  Inline,
  MultiSelect,
  Paragraph,
  Slider,
  Stack,
  Status,
  Tooltip,
} from "@eworkit/iris";
import { useBreakpointIndex } from "@theme-ui/match-media";
import { Form, Formik, FormikProps } from "formik";
import React, { FC, memo, useCallback, useContext, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { DialogDisclosure, useDialogState } from "reakit/Dialog";
import { useRouter } from "router";
import { jsx } from "theme-ui";

import { UserContext } from "@/components/User";
import { fetchCodelist, ICodelist } from "@/data/codelists";
import { IJobRequestFilter, IJobRequestURLFilter } from "@/data/job-requests";
import { fetchSkillsList } from "@/data/skills";
import { useCodelistTranslation } from "@/hooks/Codelist";
import { ICodelistGenericSelectParams, IGroupedLanguages } from "@/views/components/Codelists";

import { useJobRequestsFilters } from "./JobRequestsFiltersContext";
import { JobRequestsMainFilters } from "./JobRequestsMainFilters";
import { ISaveSearchModalButtonProps, SaveSearchModalButton } from "./SaveSearchModal";
import { jobRequestListSearchParamsType } from "./types";
import { useSavedSearchesActions } from "./useSavedSearchesActions";

export interface JobRequestsFiltersProps {
  publicMode?: boolean;

  onSearchSaved?: (filter: IJobRequestFilter) => void;
  onSearchDeleted?: (id: number) => void;
}

export type JobRequestFiltersFormValues = Partial<IJobRequestURLFilter>;

interface JobRequestFiltersFormProps {
  form: FormikProps<JobRequestFiltersFormValues>;
}
export const JobRequestFiltersForm: FC<JobRequestFiltersFormProps> = ({}) => {
  const { filters, handleFilterChange, applyFilters } = useJobRequestsFilters();

  return (
    <Form>
      <JobRequestsMainFilters
        queryValue={filters.query}
        onQueryChange={handleFilterChange("query")}
        locationValue={filters.location}
        onLocationChange={handleFilterChange("location")}
        radiusValue={filters.radius}
        onRadiusChange={handleFilterChange("radius")}
        onSearchButtonClick={applyFilters}
      />
    </Form>
  );
};

interface JobRequestFiltersFormikProps extends JobRequestFiltersFormProps {
  initialValues?: JobRequestFiltersFormValues;
  onSubmit: () => void;
}

export const JobRequestFiltersFormik: FC<Omit<JobRequestFiltersFormikProps, "form">> = memo(
  ({ initialValues = {}, onSubmit, ...rest }) => (
    <Formik initialValues={initialValues} onSubmit={onSubmit}>
      {(form: FormikProps<Partial<IJobRequestURLFilter>>) => <JobRequestFiltersForm form={form} {...rest} />}
    </Formik>
  ),
);

export interface ISkillOption {
  value: number;
  label: string;
}
export const JobRequestsFilters: FC<JobRequestsFiltersProps> = ({ publicMode, onSearchSaved, onSearchDeleted }) => {
  const [languageOptions, setLanguageOptions] = useState<ICodelist[]>([]);
  const [skillsOptions, setSkillsOptions] = useState<ISkillOption[]>([]);
  const [skillQuery, setSkillQuery] = useState("");
  const { contextCompany } = useContext(UserContext);
  const tCodelist = useCodelistTranslation();
  const { t } = useTranslation(["public", "session"]);

  const { searchParams } = useRouter({
    ensureSearchParams: jobRequestListSearchParamsType,
  });

  const breakpoint = useBreakpointIndex();

  const { filters, empty, savedSearchChanged, additionalFiltersCount, handleFilterChange, applyFilters, clearFilters } =
    useJobRequestsFilters();

  const { updateSearch } = useSavedSearchesActions();

  const modalState = useDialogState({ animated: true });

  const remoteOptions: ICodelist[] = [
    { code: "NO_REMOTE", parameter: "" },
    { code: "PARTIALLY_REMOTE", parameter: "" },
    { code: "FULLY_REMOTE", parameter: "" },
  ];

  const levelOptions: ICodelist[] = [
    { code: "JUNIOR", parameter: "" },
    { code: "REGULAR", parameter: "" },
    { code: "SENIOR", parameter: "" },
    { code: "EXPERT", parameter: "" },
  ];

  useEffect(() => {
    const fetchLanguages = async () => {
      const options = await fetchCodelist(LANGUAGES_CODELIST_QUERY);
      const response = [{ options }] as IGroupedLanguages[];
      setLanguageOptions(groupAndTranslate(response));
    };
    fetchLanguages();
  }, []);

  useEffect(() => {
    const fetchSkills = async () => {
      const results = await fetchSkillsList({ query: skillQuery, size: 30 });
      const skills: ISkillOption[] = [];
      if (results.content.length > 0) {
        results.content.map(item => {
          const tmp: ISkillOption = {
            value: item.id,
            label: item.name,
          };
          skills.push(tmp);
        });
      }
      setSkillsOptions(skills);
    };
    fetchSkills();
  }, [skillQuery]);

  const groupAndTranslate = ([{ options }]: IGroupedLanguages[]) => {
    const tt: ICodelist[] = [];
    options.forEach(itm => {
      const tmp: ICodelist = {
        code: itm.code,
        parameter: itm.value,
      };
      tt.push(tmp);
    });
    return tt;
  };

  const LANGUAGES_CODELIST_QUERY: ICodelistGenericSelectParams = { codelistName: "languages" };

  const onlyExclusiveOption = { value: 1, label: t("LANDING_PAGE.JOB_REQUEST.LIST.FILTERS.ONLY_EXCLUSIVE") };

  const handleSaveButtonClick = useCallback(async () => {
    const newSearch = await updateSearch(filters);

    if (newSearch) {
      const skillIds = filters.skillIds?.map(s => ({
        id: s.value,
        name: s.label,
        signature: "",
      }));
      newSearch.items.skills = skillIds;

      onSearchSaved?.(newSearch);
    }
  }, [filters, updateSearch, onSearchSaved]);

  const handleViewResultsButtonClick = useCallback(() => {
    applyFilters();
    modalState.hide();
  }, [applyFilters, modalState]);

  const showSaveButtons = !publicMode && searchParams.tab !== "recommended" && searchParams.tab !== "my-saved-jobs";

  const saveButton = savedSearchChanged && (
    <Button
      sx={{ backgroundColor: "transparent" }}
      variant="secondary"
      size={["md", null, null, "sm"]}
      onClick={handleSaveButtonClick}>
      {t("session:JOB_REQUESTS.LIST.FILTERS.BUTTON_SAVE")}
    </Button>
  );

  const saveSearchModalButtonProps: ISaveSearchModalButtonProps = {
    sx: { backgroundColor: "transparent" },
    variant: "secondary",
    size: breakpoint >= 3 ? "sm" : "md",
    dialogProps: {
      onSearchSaved,
      onSearchDeleted,
    },
  };

  const saveAsButtonWithoutTooltip = (
    <SaveSearchModalButton {...saveSearchModalButtonProps}>
      {t("session:JOB_REQUESTS.LIST.FILTERS.BUTTON_SAVE_AS")}
    </SaveSearchModalButton>
  );

  const saveAsButtonWithTooltip = (
    <Tooltip content={t("session:JOB_REQUESTS.LIST.FILTERS.TOOLTIP_SAVE_SEARCH")} placement="top">
      {tooltipProps => (
        <Flex ref={tooltipProps.ref} onMouseEnter={tooltipProps.onMouseEnter} onMouseLeave={tooltipProps.onMouseLeave}>
          <SaveSearchModalButton
            onClick={tooltipProps.onMouseLeave}
            style={{ width: "100%" }}
            {...saveSearchModalButtonProps}>
            {t("session:JOB_REQUESTS.LIST.FILTERS.BUTTON_SAVE_SEARCH")}
          </SaveSearchModalButton>
        </Flex>
      )}
    </Tooltip>
  );

  const saveAsButton = filters.signature ? saveAsButtonWithoutTooltip : saveAsButtonWithTooltip;

  return (
    <Stack gap="sm">
      <JobRequestFiltersFormik onSubmit={applyFilters} initialValues={filters} />

      {breakpoint >= 3 ? (
        <Flex justifyContent="space-between">
          <Inline gap="xs">
            <MultiSelect
              placeholder={t("LANDING_PAGE.JOB_REQUEST.LIST.FILTERS.ON_SITE_REMOTE")}
              options={remoteOptions}
              identifierSelector={c => c.code}
              labelSelector={c => tCodelist("remote-work", c.code)}
              value={filters.remote}
              onChange={handleFilterChange("remote")}
              sx={{ maxWidth: "130px" }}
            />

            {contextCompany?.id && (
              <MultiSelect
                placeholder={t("LANDING_PAGE.JOB_REQUEST.LIST.FILTERS.EXCLUSIVE_FILTER")}
                options={[onlyExclusiveOption]}
                value={filters.dedicated ? [onlyExclusiveOption] : undefined}
                onChange={v => handleFilterChange("dedicated")(!!v[0])}
                sx={{ maxWidth: "130px" }}
              />
            )}

            <MultiSelect
              placeholder={"Language"}
              identifierSelector={c => c.code}
              labelSelector={c => tCodelist("languages", c.code)}
              options={languageOptions}
              value={filters.languages}
              onChange={handleFilterChange("languages")}
              searchable={true}
              sx={{ maxWidth: "130px" }}
            />

            <MultiSelect
              placeholder={"Seniority"}
              options={levelOptions}
              identifierSelector={c => c.code}
              labelSelector={c => tCodelist("skill-role-levels", c.code)}
              value={filters.level}
              onChange={handleFilterChange("level")}
              sx={{ maxWidth: "130px" }}
            />

            <MultiSelect
              placeholder={"Skills"}
              searchable={true}
              identifierSelector={c => c.value}
              labelSelector={c => c.label}
              options={skillsOptions}
              value={filters.skillIds}
              onQueryChange={e => setSkillQuery(e)}
              onChange={handleFilterChange("skillIds")}
              sx={{ maxWidth: "130px" }}
            />

            {!empty && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                {t("LANDING_PAGE.JOB_REQUEST.LIST.FILTERS.BUTTON_CLEAR_ALL")}
              </Button>
            )}
          </Inline>

          {showSaveButtons && (
            <Box>
              <Inline gap="xs">
                {saveButton}
                {saveAsButton}
              </Inline>
            </Box>
          )}
        </Flex>
      ) : (
        <React.Fragment>
          {createPortal(
            <Flex
              sx={{ position: "fixed", bottom: "md", left: 0, right: 0, zIndex: "floatingActions" }}
              justifyContent="center">
              <Button
                as={DialogDisclosure}
                iconLeft={<FiltersIcon />}
                iconRight={additionalFiltersCount > 0 ? <Status>{additionalFiltersCount}</Status> : undefined}
                {...modalState}>
                {t("LANDING_PAGE.JOB_REQUEST.LIST.FILTERS.BUTTON_FILTERS")}
              </Button>
            </Flex>,
            document.body,
          )}

          <FullScreenModal dialogState={modalState}>
            <FullScreenModalContainer>
              <FullScreenModalHeader
                sx={{ "> [data-dialog-header-main='true']": { alignItems: "center" } }}
                onCloseClick={modalState.hide}
                closeButtonAriaLabel={t("LANDING_PAGE.JOB_REQUEST.LIST.FILTERS.CLOSE")}>
                <Flex gap="xs">
                  <FullScreenModalHeading size="h-sm">
                    {t("LANDING_PAGE.JOB_REQUEST.LIST.FILTERS.FILTERS")}
                  </FullScreenModalHeading>

                  {!empty && (
                    <Button variant="ghost" size="sm" onClick={clearFilters}>
                      {t("LANDING_PAGE.JOB_REQUEST.LIST.FILTERS.BUTTON_CLEAR_ALL")}
                    </Button>
                  )}
                </Flex>
              </FullScreenModalHeader>

              <Box p="sm">
                <Stack gap="lg">
                  <Stack gap="md">
                    {filters.location?.city && (
                      <FormGroup
                        label={
                          <Flex justifyContent="space-between">
                            <FormLabel label={t("LANDING_PAGE.JOB_REQUEST.LIST.FILTERS.LOCATION_RADIUS")} />
                            <Paragraph bold>
                              {t("LANDING_PAGE.JOB_REQUEST.LIST.FILTERS.KM", {
                                value: filters.radius ? filters.radius / 1000 : 0,
                              })}
                            </Paragraph>
                          </Flex>
                        }>
                        <Stack>
                          <Slider
                            min={0}
                            max={150}
                            step={5}
                            value={filters.radius ? filters.radius / 1000 : 0}
                            onChange={v => handleFilterChange("radius")(v * 1000)}
                          />
                          <Flex justifyContent="space-between">
                            <Paragraph>{t("LANDING_PAGE.JOB_REQUEST.LIST.FILTERS.KM", { value: 0 })}</Paragraph>
                            <Paragraph>{t("LANDING_PAGE.JOB_REQUEST.LIST.FILTERS.KM", { value: 150 })}</Paragraph>
                          </Flex>
                        </Stack>
                      </FormGroup>
                    )}

                    <FormGroup label={<FormLabel label={t("LANDING_PAGE.JOB_REQUEST.LIST.FILTERS.ON_SITE_REMOTE")} />}>
                      <Stack gap="xs">
                        {remoteOptions.map(o => (
                          <label key={o.code}>
                            <Inline alignItems="center" gap="xs">
                              <Checkbox
                                checked={!!filters.remote?.find(r => r.code === o.code)}
                                onChange={v =>
                                  handleFilterChange("remote")(
                                    v ? [...(filters.remote ?? []), o] : filters.remote?.filter(r => r.code !== o.code),
                                  )
                                }
                              />
                              <Paragraph>{tCodelist("remote-work", o.code)}</Paragraph>
                            </Inline>
                          </label>
                        ))}
                      </Stack>
                    </FormGroup>

                    {contextCompany?.id && (
                      <FormGroup
                        label={<FormLabel label={t("LANDING_PAGE.JOB_REQUEST.LIST.FILTERS.EXCLUSIVE_FILTER")} />}>
                        <label>
                          <Inline alignItems="center" gap="xs">
                            <Checkbox checked={filters.dedicated} onChange={handleFilterChange("dedicated")} />
                            <Paragraph>{t("LANDING_PAGE.JOB_REQUEST.LIST.FILTERS.ONLY_EXCLUSIVE")}</Paragraph>
                          </Inline>
                        </label>
                      </FormGroup>
                    )}
                  </Stack>

                  <FormGroup label={<FormLabel label={"Languages"} />}>
                    <Stack gap="xs">
                      <MultiSelect
                        placeholder={"Language"}
                        identifierSelector={c => c.code}
                        labelSelector={c => tCodelist("languages", c.code)}
                        options={languageOptions}
                        value={filters.languages}
                        onChange={handleFilterChange("languages")}
                        searchable={true}
                      />
                    </Stack>
                  </FormGroup>

                  <FormGroup label={<FormLabel label={"Seniority"} />}>
                    <Stack gap="xs">
                      <MultiSelect
                        placeholder={"Seniority"}
                        options={levelOptions}
                        identifierSelector={c => c.code}
                        labelSelector={c => tCodelist("skill-role-levels", c.code)}
                        value={filters.level}
                        onChange={handleFilterChange("level")}
                      />
                    </Stack>
                  </FormGroup>

                  <FormGroup label={<FormLabel label={"Skills"} />}>
                    <Stack gap="xs">
                      <MultiSelect
                        placeholder={"Skills"}
                        searchable={true}
                        identifierSelector={c => c.value}
                        labelSelector={c => c.label}
                        options={skillsOptions}
                        value={filters.skillIds}
                        onQueryChange={e => setSkillQuery(e)}
                        onChange={handleFilterChange("skillIds")}
                      />
                    </Stack>
                  </FormGroup>

                  <Stack gap="xs">
                    <Button onClick={handleViewResultsButtonClick}>
                      {t("LANDING_PAGE.JOB_REQUEST.LIST.FILTERS.BUTTON_VIEW_RESULTS")}
                    </Button>

                    {showSaveButtons && (
                      <React.Fragment>
                        {saveAsButton}
                        {saveButton}
                      </React.Fragment>
                    )}
                  </Stack>
                </Stack>
              </Box>
            </FullScreenModalContainer>
          </FullScreenModal>
        </React.Fragment>
      )}
    </Stack>
  );
};
